import { type ReactNode } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '@claim-os/shared';
import { NoOrganizationError } from './NoOrganizationError';

interface AuthGuardProps {
  children: ReactNode;
}

/**
 * Route protection component.
 * Shows loading state during authentication, renders children when authenticated.
 * Handles error states including no organization membership.
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const { isLoading, isAuthenticated, isError, errorMessage, user } = useAuth();

  // Show loading spinner during initialization
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">
          Signing in...
        </Typography>
      </Box>
    );
  }

  // Handle no organization error
  if (isError && errorMessage === 'NO_ORGANIZATION') {
    return <NoOrganizationError />;
  }

  // Handle service unavailable error
  if (isError && errorMessage === 'SERVICE_UNAVAILABLE') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
          p: 3,
        }}
      >
        <Typography variant="h5" color="error">
          Service Unavailable
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          We&apos;re unable to connect to the authentication service.
          <br />
          Please try again later.
        </Typography>
      </Box>
    );
  }

  // Handle generic auth error
  if (isError) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
          p: 3,
        }}
      >
        <Typography variant="h5" color="error">
          Authentication Error
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {errorMessage || 'An error occurred during authentication.'}
        </Typography>
      </Box>
    );
  }

  // User is authenticated with valid organization
  if (isAuthenticated && user) {
    return <>{children}</>;
  }

  // Fallback - should not reach here with login-required mode
  // Keycloak will redirect to login automatically
  return null;
}
